import axios from 'axios';
import { RoleGroup } from '../components/Roles/_core/_model';
import { getAPIUrl } from '../Constants.ts';
import { ApproveRejctRequest, AppUserResponse, AuditDetails, AuditItem, AuditObjectTypes, ColumnSecurityPolicy, DimensionFieldsModel, DimensionModel, FilterResponseModel, GetUserRequest, JobRunHistory, NightlyJobConfigModel, RoleRequestParams, RoleResponseModel, TableColumns, UpsertUserRoleRequest, UserModel, UserRequestGetParam, UserRequestTable, UserRoles } from './_model';

const API_URL = getAPIUrl();

axios.interceptors.request.use(
    (config) => {
        // Grab the token from sessionStorage (where we saved it during login)
        const token = sessionStorage.getItem('token') || localStorage.getItem('token');

        // If the token exists, attach it to the Authorization header
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// ==========================================
// 2. RESPONSE INTERCEPTOR: Handle Expired Tokens (Optional but highly recommended)
// ==========================================
axios.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        // If the backend returns a 401 Unauthorized, the token is likely expired or invalid.
        if (error.response && error.response.status === 401) {
            const originalRequestUrl = error.config?.url || '';
            if (originalRequestUrl.includes('/auth/login')) {
                return Promise.reject(error);
            }
            if (!window.location.pathname.includes('/login')) {
                console.warn("Token expired or invalid. Logging out...");
                sessionStorage.clear();
                localStorage.removeItem('token');
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

const LOGIN_URL = `${API_URL}/auth/login`;
const AD_LOGIN_URL = `${API_URL}/auth/ad-login`;
const RESET_PASSWORD = `${API_URL}/auth/reset-password`;
const GET_DIMENSION = `${API_URL}/values/GetDimension`;
const GET_DIMENSON_FIELDS = `${API_URL}/values/GetDimensionFields`
const GET_ROLES = `${API_URL}/values/GetRolesEx`
const GET_FILTER = `${API_URL}/values/GetFilter`;
const GET_USER_REQUEST = `${API_URL}/values/GetUserRequestEx`;
const APPROVE_OR_REJECT = `${API_URL}/values/ApproveOrRejectUser`;
const GET_APP_USER = `${API_URL}/values/GetAppUser`;
const GET_USERS = `${API_URL}/values/GetUserEx`;
const GET_USER_ROLES = `${API_URL}/values/GetUserRoles`;
const UPSERT_USER_ROLES = `${API_URL}/values/AddOrRemoveUserRoles`;
const GET_ROLE_BY_CONDITION = `${API_URL}/values/GetRoleByCondition`;
const DELETE_ROLE = `${API_URL}/values/deleteRole`;
const DELETE_USER = `${API_URL}/values/deleteUser`;
const DELETE_ROLE_GROUP = `${API_URL}/values/deleteRoleGroup`;
const DELETE_APPROVAL = `${API_URL}/values/deleteUserRequest`;
const UPSERT_DIMENSION = `${API_URL}/values/UpsertDimension`;
const GET_USERS_GROUPS = `${API_URL}/values/GetUserRoleGroup`;
const ADD_REMOVE_ROLE_GROUP = `${API_URL}/values/AddOrRemoveUserRoleGroup`;
const GET_USER_BY_ROLEGROUP = `${API_URL}/values/GetUserByRoleGroup`;
const GET_USER_BY_ROLE = `${API_URL}/values/GetUsersByRole`;
const GET_OBJECT_AUDIT = `${API_URL}/values/GetObjectAudit`;
const GET_OBJECT_AUDIT_DETAILS = `${API_URL}/values/GetObjectAuditDetails`;
const GET_OBJECT_FIELDS = `${API_URL}/values/GetObjectFields`;
const UPSERT_TABLE_COLUMNS = `${API_URL}/values/UpsertObjectFields`;
const GET_FIELD_VALUES = `${API_URL}/values/GetDimensionFieldValues`;
const UPSERT_FIELD = `${API_URL}/values/UpdateDimensionFieldType`;
const FORGOT_PASSWORD_URL = `${API_URL}/auth/forgot-password`;
const SET_PASSWORD_URL = `${API_URL}/auth/set-password`;
const UPDATE_PASSWORD_URL = `${API_URL}/auth/update-password`;
const GET_NIGHTLY_CONFIG = `${API_URL}/values/GetNightlyJobConfig`;
const UPDATE_NIGHTLY_CONFIG = `${API_URL}/values/UpdateNightlyJobConfig`;
const GET_NIGHTLY_HISTORY = `${API_URL}/values/GetNightlyJobHistory`;
const TRIGGER_NIGHTLY_JOB = `${API_URL}/values/TriggerNightlyJob`;

const GET_POLICIES_URL = `${API_URL}/values/GetColumnSecurityPolicies`;
const UPSERT_POLICY_URL = `${API_URL}/values/UpsertColumnSecurityPolicy`;
const DELETE_POLICY_URL = `${API_URL}/values/DeleteColumnSecurityPolicy`;

export function LoginUser(request: { Email: string, Password: string, Username?: string }) {
    return axios.post<{ token: string, userId: string, username: string, role: string }>(LOGIN_URL, request);
}

export function AdLoginUser(request: { Email: string, MsToken: string }) {
    return axios.post<{ token: string, userId: string, username: string, role: string, requiresPasswordReset: boolean }>(AD_LOGIN_URL, request);
}

export function ResetPassword(request: { UserIds: number[] }) {
    return axios.post<{
        message: string;
        successfulCount: number;
        failedCount: number;
        successful: string[];
        failed: string[];
    }>(RESET_PASSWORD, request);
}

export function UpdatePasswordRequest(request: { UserId: number, OldPassword: string, NewPassword: string }) {
    return axios.post<{ message: string }>(UPDATE_PASSWORD_URL, request);
}

export function ForgotPasswordRequest(request: { Email: string }) {
    return axios.post<{ message: string }>(FORGOT_PASSWORD_URL, request);
}

export function SetPasswordRequest(request: { Email: string, Token: string, NewPassword: string }) {
    return axios.post<{ message: string }>(SET_PASSWORD_URL, request);
}

export function UpsertDimensionField(request: DimensionFieldsModel) {
    return axios.post<string>(UPSERT_FIELD, request);
}

export function UpsertTableColumn(request: TableColumns[]) {
    return axios.post<string>(UPSERT_TABLE_COLUMNS, request);
}

export function GetTableColumns(objectId: AuditObjectTypes) {
    return axios.get<TableColumns[]>(GET_OBJECT_FIELDS + "?objectId=" + objectId)
}

export function GetObjectAuditDetailss(aAuditItemId: number) {
    return axios.get<AuditDetails[]>(GET_OBJECT_AUDIT_DETAILS + `?aAuditItemId=${aAuditItemId}`)
}

export function GetObjectAudits(objectType: AuditObjectTypes, instanceId: number, numberOfInstance: number) {
    return axios.get<AuditItem[]>(GET_OBJECT_AUDIT + `?objectType=${objectType}&instanceId=${instanceId}&numberOfInstance=${numberOfInstance}`);
}

export function GetUserByRole(roleIDS: string) {
    return axios.get<{ roleID: number, userID: number, dataMapCode: string }[]>(GET_USER_BY_ROLE + "?roleIDS=" + roleIDS);
}

export function GetUsersByRoleGroup(roleGroupId: string) {
    return axios.get<{ RoleGroupID: number, userID: number }[]>(GET_USER_BY_ROLEGROUP + "?RoleGroupID=" + roleGroupId);
}


export function AddRemoveUsersRoleGroup(request: { userID: number, roleGroups: number[], byUser: number, prevGroups: number[] }) {
    return axios.post<string>(ADD_REMOVE_ROLE_GROUP, request)
}

export function GerUsersRoleGroup(userID: number) {
    return axios.get<RoleGroup[]>(GET_USERS_GROUPS + "?userID=" + userID)
}

export function UpsertDimension(request: DimensionModel) {
    return axios.post<string>(UPSERT_DIMENSION, request);
}

export function DeleteApproval(stagingUserID: string, byUser: number) {
    return axios.get<string>(DELETE_APPROVAL + "?stagingUserID=" + stagingUserID + "&byUser=" + byUser);
}

export function DeleteRole(roleId: string, byUser: number) {
    return axios.get<string>(DELETE_ROLE + "?RoleID=" + roleId + "&byUser=" + byUser);
}

export function DeleteUser(userID: string, byUser: number) {
    return axios.get<string>(DELETE_USER + "?userID=" + userID + "&byUser=" + byUser);
}

export function DeleteRoleGroup(roleGroupId: string, byUser: number) {
    return axios.get<string>(DELETE_ROLE_GROUP + "?roleGroupId=" + roleGroupId + "&byUser=" + byUser);
}

export function UpdateUserRoles(request: UpsertUserRoleRequest) {
    return axios.post<string>(UPSERT_USER_ROLES, request);
}

export function GetUserRoles(userID: number) {
    return axios.get<UserRoles[]>(GET_USER_ROLES + "?userID=" + userID)
}
export function GetUsers(request: GetUserRequest) {
    return axios.post<UserModel[]>(GET_USERS, request);
}

export function GetAppUser(userID: number) {
    return axios.get<AppUserResponse[]>(GET_APP_USER + "?userID=" + userID);
}

export function ApproveOrReject(request: ApproveRejctRequest) {
    return axios.post<string>(APPROVE_OR_REJECT, request);
}

export function GetUserRequests(request: UserRequestGetParam) {
    return axios.post<UserRequestTable[]>(GET_USER_REQUEST, request)
}

export function GetDimension() {
    return axios.get<DimensionModel[]>(GET_DIMENSION);
}

export function GetDimensionFields(dimensionCode: string) {
    return axios.get<DimensionFieldsModel[]>(GET_DIMENSON_FIELDS + "?DemensionCode=" + dimensionCode)
}

export function GetDimensionFieldValues(request: {
    columnName: string,
    forDimension: string
}) {
    return axios.post<string[]>(GET_FIELD_VALUES, request);
}

export function GetRoles(request: RoleRequestParams) {
    return axios.post<RoleResponseModel[]>(GET_ROLES, request);
}

export function GetRoleByCondition(roleCondition: string, forDimension: string) {
    return axios.get<number>(GET_ROLE_BY_CONDITION + "?roleCondition=" + roleCondition + "&forDimension=" + forDimension);
}

export function GetFilters(request: {
    roleID: number
}) {
    return axios.post<FilterResponseModel[]>(GET_FILTER, request)
}


export function GetNightlyJobConfig() {
    return axios.get<NightlyJobConfigModel>(GET_NIGHTLY_CONFIG);
}

export function UpdateNightlyJobConfig(request: NightlyJobConfigModel) {
    return axios.post<string>(UPDATE_NIGHTLY_CONFIG, request);
}

export function GetNightlyJobHistory(limit: number = 30) {
    return axios.get<JobRunHistory[]>(`${GET_NIGHTLY_HISTORY}?limit=${limit}`);
}

export function TriggerNightlyJob() {
    return axios.post<string>(TRIGGER_NIGHTLY_JOB);
}
export async function GetColumnSecurityPolicies(): Promise<AxiosResponse<ColumnSecurityPolicy[]>> {
    try {
        return await axios.get<ColumnSecurityPolicy[]>(GET_POLICIES_URL);
    } catch (error) {
        console.warn("[API Fallback] GetColumnSecurityPolicies failed. Returning mock data.");
        return new Promise(resolve => setTimeout(() => resolve({
            data: [...mockPolicies], status: 200, statusText: "OK", headers: {}, config: {} as any
        }), 500));
    }
}

export async function UpsertColumnSecurityPolicy(policy: ColumnSecurityPolicy): Promise<AxiosResponse<string>> {
    try {
        return await axios.post<string>(UPSERT_POLICY_URL, policy);
    } catch (error) {
        console.warn("[API Fallback] UpsertColumnSecurityPolicy failed. Updating mock state.");
        return new Promise(resolve => setTimeout(() => {
            const index = mockPolicies.findIndex(p => p.policyID === policy.policyID);
            if (index > -1) {
                mockPolicies[index] = policy;
            } else {
                mockPolicies.push({ ...policy, policyID: `pol-${Date.now()}` });
            }
            resolve({ data: "Success", status: 200, statusText: "OK", headers: {}, config: {} as any });
        }, 500));
    }
}

export async function DeleteColumnSecurityPolicy(policyID: string, byUser: number): Promise<AxiosResponse<string>> {
    try {
        return await axios.get<string>(`${DELETE_POLICY_URL}?policyID=${policyID}&byUser=${byUser}`);
    } catch (error) {
        console.warn("[API Fallback] DeleteColumnSecurityPolicy failed. Simulating deletion.");
        return new Promise(resolve => setTimeout(() => {
            mockPolicies = mockPolicies.filter(p => p.policyID !== policyID);
            resolve({ data: "Success", status: 200, statusText: "OK", headers: {}, config: {} as any });
        }, 500));
    }
}